/**
 * Reading a plan page's markdown by section, in one place.
 *
 * The plan page is written by the backend one section at a time
 * (`workItems/planPage.ts`, `## <title>` per section) and two frontend readers
 * need to walk it: the amendment diff, which compares the page section by
 * section against a proposed rewrite, and the question block, which lifts the
 * "Open questions" section out of the body so it can be answered rather than
 * only read. Those two started as one private splitter inside AmendmentCards;
 * a second copy for the questions would be two parsers for one document, which
 * is how a diff and a question list come to disagree about where a section ends.
 *
 * This module is deliberately dumb: it splits on the heading level the page
 * uses and nothing else. It does not know which sections exist, and a page with
 * no headings at all reads as one nameless opening section.
 */

export interface MarkdownSection {
  /** The heading text, or "" for the lines before the first heading. */
  title: string;
  lines: string[];
}

const HEADING = /^##\s+/;

export function splitSections(markdown: string): MarkdownSection[] {
  const sections: MarkdownSection[] = [{ title: "", lines: [] }];
  for (const line of markdown.replace(/\r\n/g, "\n").split("\n")) {
    if (HEADING.test(line)) sections.push({ title: line.replace(HEADING, "").trim(), lines: [] });
    else sections[sections.length - 1]!.lines.push(line);
  }
  return sections;
}

/** The lines of one section, matched case-insensitively. Empty when absent. */
export function sectionLines(markdown: string, title: string): string[] {
  const wanted = title.trim().toLowerCase();
  for (const section of splitSections(markdown)) {
    if (section.title.toLowerCase() === wanted) return section.lines;
  }
  return [];
}

/**
 * The same markdown with one section removed, heading included.
 *
 * Display-only, and only ever used that way: the questions are rendered as
 * answerable rows below the body, so leaving them in the body too would state
 * the same question twice. Nothing downstream reads the stripped copy. The
 * amendment cards fetch the page themselves for their diff, so the real
 * document is what they compare against.
 */
export function stripSection(markdown: string, title: string): string {
  const wanted = title.trim().toLowerCase();
  const kept: string[] = [];
  let dropping = false;
  for (const line of markdown.replace(/\r\n/g, "\n").split("\n")) {
    if (HEADING.test(line)) {
      dropping = line.replace(HEADING, "").trim().toLowerCase() === wanted;
      if (dropping) continue;
    }
    if (!dropping) kept.push(line);
  }
  return kept.join("\n").replace(/\n{3,}$/, "\n");
}
